import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Megaphone, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const AnnouncementsPage = () => {
  const { token, user } = useContext(AuthContext);
  
  // Helper to check user roles
  const isHQAdmin = user?.role === 'HQ_ADMIN';
  
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'Normal'
  });

  useEffect(() => {
    fetchAnnouncements();
  }, [token]);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/announcements`, {
        ...formData,
        created_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement created successfully');
      setOpenDialog(false);
      fetchAnnouncements();
      setFormData({ title: '', content: '', priority: 'Normal' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create announcement');
    }
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'High') return <AlertTriangle className="h-5 w-5 text-destructive" />;
    if (priority === 'Low') return <Info className="h-5 w-5 text-muted-foreground" />;
    return <Megaphone className="h-5 w-5 text-primary" />;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="announcements-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-heading font-bold">Announcements</h1>
            <p className="text-muted-foreground mt-1">Important updates and notices</p>
          </div>
          {isHQAdmin && (
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-announcement-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Announcement
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Announcement</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title*</Label>
                    <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Content*</Label>
                    <Textarea rows={5} value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} required data-testid="content-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Priority*</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Normal">Normal</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-announcement-btn">Create Announcement</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="space-y-4">
          {loading ? (
            <p className="text-center text-muted-foreground">Loading...</p>
          ) : announcements.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No announcements yet</p>
              </CardContent>
            </Card>
          ) : (
            announcements.map((announcement) => (
              <Card key={announcement.announcement_id} className="border border-border/60 rounded-xl shadow-sm" data-testid={`announcement-${announcement.title}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getPriorityIcon(announcement.priority)}
                      <div className="flex-1">
                        <CardTitle className="text-lg">{announcement.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                          <span>Posted by {announcement.created_by}</span>
                          <span>•</span>
                          <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <Badge variant={announcement.priority === 'High' ? 'destructive' : 'secondary'}>
                      {announcement.priority}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground whitespace-pre-wrap">{announcement.content}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AnnouncementsPage;